import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import ApiClientPanel from "@/pages/admin/apikeys/components/ApiClientPanel";
import ApiCallLogPanel from "@/pages/admin/apikeys/components/ApiCallLogPanel";
import ApiUsageInsights from "@/pages/admin/apikeys/components/ApiUsageInsights";
import ApiPolicySwitch from "@/pages/admin/apikeys/components/ApiPolicySwitch";
import ApiPolicyHistory from "@/pages/admin/apikeys/components/ApiPolicyHistory";
import ApiClientModal from "@/pages/admin/apikeys/components/ApiClientModal";
import ApiKeyModal from "@/pages/admin/apikeys/components/ApiKeyModal";
import { useApiKeys, type ApiClient, type ApiKey } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface Notice {
  text: string;
  tone: "success" | "error";
}

interface PendingDelete {
  kind: "client" | "key";
  id: number;
  name: string;
}

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const {
    clients,
    keys,
    logs,
    policy,
    policyHistory,
    loading,
    error,
    reload,
    setPolicy,
    createClient,
    updateClient,
    setClientStatus,
    deleteClient,
    createKey,
    setKeyStatus,
    deleteKey,
  } = useApiKeys();

  const [logClient, setLogClient] = useState<number | "all">("all");

  const [notice, setNotice] = useState<Notice | null>(null);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ApiClient | null>(null);
  const [keyModalClient, setKeyModalClient] = useState<ApiClient | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleting, setDeleting] = useState(false);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const stats = useMemo(() => {
    const active24h = logs.filter((log) => Date.now() - new Date(log.created_at).getTime() <= 86400000);
    const failed = active24h.filter((log) => log.valid !== true).length;
    const errorRate = active24h.length === 0 ? 0 : (failed / active24h.length) * 100;
    return {
      clients: clients.length,
      activeKeys: keys.filter((key) => key.status === "active").length,
      calls24h: active24h.length,
      errorRate: errorRate.toFixed(1),
    };
  }, [clients, keys, logs]);

  const cards = [
    { label: t("apikeys.statClients"), value: String(stats.clients), icon: "ri-plug-2-line", tone: "primary" },
    { label: t("apikeys.statActiveKeys"), value: String(stats.activeKeys), icon: "ri-key-2-line", tone: "secondary" },
    { label: t("apikeys.statCalls24h"), value: stats.calls24h.toLocaleString(), icon: "ri-pulse-line", tone: "accent" },
    { label: t("apikeys.statErrorRate"), value: `${stats.errorRate}%`, icon: "ri-error-warning-line", tone: "accent" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  const handlePolicyChange = async (mode: "required" | "optional") => {
    await setPolicy(mode);
  };

  const openCreate = () => {
    setEditingClient(null);
    setClientModalOpen(true);
  };

  const openEdit = (client: ApiClient) => {
    setEditingClient(client);
    setClientModalOpen(true);
  };

  const handleClientSubmit = async (input: {
    name: string;
    ownerName: string;
    contactEmail: string;
    description: string;
  }) => {
    if (editingClient) {
      await updateClient(editingClient.id, input);
      notify(t("apikeys.updated"), "success");
    } else {
      await createClient(input);
      notify(t("apikeys.created"), "success");
    }
    setClientModalOpen(false);
    setEditingClient(null);
  };

  const handleToggleStatus = async (client: ApiClient) => {
    try {
      const next = client.status === "active" ? "suspended" : "active";
      await setClientStatus(client.id, next);
      notify(t("apikeys.statusChanged"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("apikeys.actionFailed"), "error");
    }
  };

  const handleToggleKey = async (key: ApiKey) => {
    try {
      const next = key.status === "active" ? "revoked" : "active";
      await setKeyStatus(key.id, next);
      notify(next === "revoked" ? t("apikeys.keyRevoked") : t("apikeys.keyEnabled"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("apikeys.actionFailed"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === "client") {
        await deleteClient(pendingDelete.id);
        notify(t("apikeys.deleted"), "success");
      } else {
        await deleteKey(pendingDelete.id);
        notify(t("apikeys.keyDeleted"), "success");
      }
      setPendingDelete(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : t("apikeys.actionFailed"), "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("apikeys.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("apikeys.breadcrumbAdmin")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("apikeys.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-plug-2-line" />
                  </span>
                  {t("apikeys.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("apikeys.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("apikeys.subtitle")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/docs/integration"
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                    <i className="ri-book-open-line" />
                  </span>
                  {t("apikeys.docs")}
                </Link>
                <Link
                  to="/admin"
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                    <i className="ri-arrow-left-line" />
                  </span>
                  {t("apikeys.backToAdmin")}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("apikeys.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {cards.map((card) => (
                  <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-600">{card.label}</span>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                        <i className={`${card.icon} text-base`} />
                      </span>
                    </div>
                    <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : card.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <ApiPolicySwitch policy={policy} loading={loading} onChange={handlePolicyChange} notify={notify} />
              </div>

              <div className="mt-8">
                <ApiUsageInsights
                  logs={logs}
                  clients={clients}
                  loading={loading}
                  activeClientId={logClient}
                  onSelectClient={setLogClient}
                />
              </div>

              <div className="mt-8">
                <ApiClientPanel
                  clients={clients}
                  keys={keys}
                  logs={logs}
                  loading={loading}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onToggleStatus={handleToggleStatus}
                  onDelete={(client) => setPendingDelete({ kind: "client", id: client.id, name: client.name })}
                  onAddKey={(client) => setKeyModalClient(client)}
                  onToggleKey={handleToggleKey}
                  onDeleteKey={(key) => setPendingDelete({ kind: "key", id: key.id, name: key.name })}
                />
              </div>

              <div className="mt-8" id="api-call-logs">
                <ApiCallLogPanel
                  logs={logs}
                  clients={clients}
                  keys={keys}
                  loading={loading}
                  clientFilter={logClient}
                  onClientFilterChange={setLogClient}
                />
              </div>

              <div className="mt-8">
                <ApiPolicyHistory history={policyHistory} loading={loading} />
              </div>
            </>
          )}
        </section>
      </main>

      <ApiClientModal
        open={clientModalOpen}
        initial={editingClient}
        onClose={() => {
          setClientModalOpen(false);
          setEditingClient(null);
        }}
        onSubmit={handleClientSubmit}
        notify={notify}
      />

      <ApiKeyModal
        open={keyModalClient !== null}
        client={keyModalClient}
        onClose={() => setKeyModalClient(null)}
        onSubmit={createKey}
        notify={notify}
      />

      {pendingDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/40 px-4">
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-5 text-center">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-delete-bin-line text-xl" />
            </span>
            <p className="mt-3 text-sm font-medium text-foreground-950">{pendingDelete.name}</p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              {pendingDelete.kind === "client" ? t("apikeys.confirmDeleteClient") : t("apikeys.confirmDeleteKey")}
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t("apikeys.cancel")}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void confirmDelete()}
                className="cursor-pointer whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("apikeys.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}